import React, { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { userService } from '../api/api';
import { useAuth } from '../auth/AuthContext';
import { MODULE_OPTIONS, canManageAccess, defaultModulePermissions, hasFullAccess, normalizeModulePermissions, normalizeRole, roleLabel, ROLE_OPTIONS } from '../auth/roles';
import ConfirmDialog from './ConfirmDialog';
import { formatPortalDate } from '../utils/dateFormat';

const ALLOWED_USER_EMAIL_DOMAIN = 'biomedwaste.com';

const emptyForm = {
  name: '',
  email: '',
  phone_number: '',
  password: '',
  role: 'driver',
  module_permissions: defaultModulePermissions('driver'),
  active: true,
};

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return formatPortalDate(date, { includeTime: true });
}

export default function UserManagement() {
  const { user: currentUser, canManageAccess: canManageUserAccess } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteUser, setDeleteUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [uploadingDocument, setUploadingDocument] = useState('');
  const [error, setError] = useState('');
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });

  const loadUsers = async () => {
    if (!canManageUserAccess) return;
    try {
      setLoading(true);
      const res = await userService.getAll();
      setUsers(res.data || []);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageUserAccess]);

  const openCreate = () => {
    setEditingUser(null);
    setForm({ ...emptyForm, module_permissions: defaultModulePermissions(emptyForm.role) });
    setDocuments([]);
    setDialogOpen(true);
    setError('');
  };

  const loadDocuments = async (userId) => {
    const res = await userService.getDocuments(userId);
    setDocuments(Array.isArray(res.data) ? res.data : []);
  };

  const openEdit = async (row) => {
    setEditingUser(row);
    setForm({
      name: row.name || '',
      email: row.email || '',
      phone_number: row.phone_number || '',
      password: '',
      role: normalizeRole(row.role),
      module_permissions: normalizeModulePermissions(row.role, row.module_permissions),
      active: row.active !== false,
    });
    setDocuments([]);
    setDialogOpen(true);
    setError('');
    try {
      await loadDocuments(row.id);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load user documents.');
    }
  };

  const update = (field) => (event) => {
    const value = field === 'active' ? event.target.checked : event.target.value;
    setForm((prev) => {
      if (field === 'role') {
        return {
          ...prev,
          role: value,
          module_permissions: defaultModulePermissions(value),
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const updateModuleAccess = (moduleName) => (event) => {
    setForm((prev) => ({
      ...prev,
      module_permissions: {
        ...normalizeModulePermissions(prev.role, prev.module_permissions),
        [moduleName]: event.target.checked,
      },
    }));
  };

  const save = async () => {
    try {
      setSaving(true);
      setError('');
      const payload = {
        name: form.name,
        email: form.email,
        phone_number: form.phone_number,
        role: form.role,
        module_permissions: normalizeModulePermissions(form.role, form.module_permissions),
        active: form.active,
      };
      if (form.password) payload.password = form.password;

      if (editingUser) {
        await userService.update(editingUser.id, payload);
      } else {
        await userService.create({ ...payload, password: form.password });
      }

      setDialogOpen(false);
      setEditingUser(null);
      await loadUsers();
      setToast({ open: true, severity: 'success', message: editingUser ? 'User updated.' : 'User created.' });
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save user.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteUser?.id) return;
    try {
      await userService.delete(deleteUser.id);
      setDeleteUser(null);
      await loadUsers();
      setToast({ open: true, severity: 'success', message: 'User deactivated.' });
    } catch (err) {
      setToast({ open: true, severity: 'error', message: err?.response?.data?.error || err?.message || 'Failed to deactivate user.' });
    }
  };

  const documentByType = (documentType, fallbackType) => (
    documents.find((document) => document.document_type === documentType)
    || (fallbackType ? documents.find((document) => document.document_type === fallbackType) : null)
  );

  const uploadDocument = (documentType) => async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !editingUser?.id) return;
    try {
      setUploadingDocument(documentType);
      setError('');
      await userService.uploadDocument(editingUser.id, documentType, file);
      await loadDocuments(editingUser.id);
      if (documentType === 'profile_photo') await loadUsers();
      setToast({ open: true, severity: 'success', message: 'User document uploaded.' });
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to upload document.');
    } finally {
      setUploadingDocument('');
    }
  };

    if (!canManageUserAccess) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Admin access is required to manage users.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.25, sm: 1.75, md: 2 } }}>
      <Card className="portal-page-header" sx={{ borderRadius: 2.25, boxShadow: 3 }}>
        <CardContent sx={{ p: { xs: 1.15, sm: 1.35, md: 1.55 }, '&:last-child': { pb: { xs: 1.15, sm: 1.35, md: 1.55 } } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: { xs: 1, sm: 1.25 }, mb: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box sx={{ width: { xs: 40, sm: 46 }, height: { xs: 40, sm: 46 }, borderRadius: 2, bgcolor: '#001f3f', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PeopleAltIcon sx={{ fontSize: { xs: 23, sm: 27 } }} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ color: '#001f3f', fontWeight: 900, fontSize: { xs: '1rem', sm: '1.18rem', md: '1.3rem' }, lineHeight: 1.05, mb: 0.1 }}>
                  User Management
                </Typography>
                <Typography color="text.secondary" sx={{ fontWeight: 700, fontSize: { xs: '0.68rem', sm: '0.76rem' }, lineHeight: 1.25 }}>
                  Create accounts, update roles, and deactivate users
                </Typography>
              </Box>
            </Box>
            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ bgcolor: '#001f3f', fontWeight: 900, px: 1.55, py: 0.62, minHeight: 36, borderRadius: 1.6, fontSize: { xs: '0.78rem', sm: '0.86rem' } }}>
              Add User
            </Button>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <TableContainer sx={{ border: '1px solid #d8e2ef', borderRadius: 2, overflowX: 'auto' }}>
            <Table sx={{ minWidth: 1150 }}>
              <TableHead>
                <TableRow sx={{ '& .MuiTableCell-head': { bgcolor: '#001f3f', color: 'white', fontWeight: 900 } }}>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone / Mobile</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Module Access</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8}>Loading users…</TableCell></TableRow>
                ) : users.length === 0 ? (
                  <TableRow><TableCell colSpan={8}>No users found.</TableCell></TableRow>
                ) : users.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
                        <Avatar
                          src={row.profile_photo?.url || undefined}
                          alt={row.name}
                          sx={{ width: 38, height: 38, bgcolor: '#d7ef23', color: '#001f3f', fontWeight: 900 }}
                        >
                          {(row.name || row.email || 'U').trim().charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography sx={{ fontWeight: 900 }}>{row.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{row.email}</TableCell>
                    <TableCell><Typography sx={{ fontWeight: row.phone_number ? 850 : 500, whiteSpace: 'nowrap' }}>{row.phone_number || '—'}</Typography></TableCell>
                    <TableCell>
                      <Chip label={roleLabel(row.role)} color={canManageAccess(row.role) ? 'primary' : 'default'} size="small" sx={{ fontWeight: 800 }} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 340 }}>
                      {canManageAccess(row.role) ? (
                        <Chip label="All modules" color="success" size="small" sx={{ fontWeight: 800 }} />
                      ) : (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {MODULE_OPTIONS.filter((module) => normalizeModulePermissions(row.role, row.module_permissions)[module.value]).slice(0, 3).map((module) => (
                            <Chip key={module.value} label={module.label} size="small" variant="outlined" sx={{ fontWeight: 700 }} />
                          ))}
                          {MODULE_OPTIONS.filter((module) => normalizeModulePermissions(row.role, row.module_permissions)[module.value]).length > 3 && (
                            <Chip label={`+${MODULE_OPTIONS.filter((module) => normalizeModulePermissions(row.role, row.module_permissions)[module.value]).length - 3}`} size="small" />
                          )}
                          {MODULE_OPTIONS.every((module) => !normalizeModulePermissions(row.role, row.module_permissions)[module.value]) && (
                            <Typography variant="caption" color="textSecondary">No modules</Typography>
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={row.active === false ? 'Inactive' : 'Active'} color={row.active === false ? 'default' : 'success'} size="small" sx={{ fontWeight: 800 }} />
                    </TableCell>
                    <TableCell>{formatDate(row.last_login_at)}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => openEdit(row)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={row.id === currentUser?.id}
                        onClick={() => setDeleteUser(row)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 900, color: '#001f3f' }}>
          {editingUser ? 'Edit User' : 'Add User'}
          <IconButton onClick={() => setDialogOpen(false)} sx={{ position: 'absolute', right: 12, top: 10 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth required label="Name" value={form.name} onChange={update('name')} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                type="email"
                label="Email"
                value={form.email}
                onChange={update('email')}
                helperText={`Only @${ALLOWED_USER_EMAIL_DOMAIN} email addresses are allowed.`}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="tel"
                label="Phone / Mobile Number"
                value={form.phone_number}
                onChange={update('phone_number')}
                inputProps={{ inputMode: 'tel', maxLength: 30 }}
                helperText="Stored internally and shown to authorized schedulers when assigning this driver."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required={!editingUser}
                type="password"
                label={editingUser ? 'New Password (optional)' : 'Password'}
                value={form.password}
                onChange={update('password')}
                helperText="Minimum 8 characters."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select label="Role" value={form.role} onChange={update('role')}>
                  {ROLE_OPTIONS.map((role) => (
                    <MenuItem key={role.value} value={role.value}>{role.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                <Switch checked={form.active} onChange={update('active')} disabled={editingUser?.id === currentUser?.id} />
                <Typography sx={{ fontWeight: 800 }}>Active</Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ p: 2, border: '1px solid #d8e2ef', borderRadius: 2, bgcolor: '#f8fbff' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#001f3f', mb: 0.5 }}>
                  Module Access
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
                  Administrators receive every module and User Management. Shipper / Receiver and Sales receive every operational module without User Management.
                </Typography>
                {canManageAccess(form.role) ? (
                  <Alert severity="info">This role has all modules and User Management access automatically.</Alert>
                ) : hasFullAccess(form.role) ? (
                  <Alert severity="success">This role has every operational module. User Management remains unavailable.</Alert>
                ) : (
                  <Grid container spacing={1}>
                    {MODULE_OPTIONS.map((module) => (
                      <Grid item xs={12} sm={6} key={module.value}>
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={normalizeModulePermissions(form.role, form.module_permissions)[module.value] === true}
                              onChange={updateModuleAccess(module.value)}
                            />
                          )}
                          label={(
                            <Box>
                              <Typography sx={{ fontWeight: 800 }}>{module.label}</Typography>
                              <Typography variant="caption" color="textSecondary">{module.description}</Typography>
                            </Box>
                          )}
                          sx={{ alignItems: 'flex-start', m: 0, p: 1, width: '100%', borderRadius: 2, '&:hover': { bgcolor: 'rgba(25,118,210,0.06)' } }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </Grid>
            {editingUser && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, border: '1px solid #d8e2ef', borderRadius: 2, bgcolor: '#fff' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#001f3f', mb: 0.5 }}>
                    Driver Documents
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
                    Global Admin and IT Admin can upload or replace the profile photograph, both sides of the driver license, and the TDG certificate. Changes use the same account records shown in the BioFleet mobile app.
                  </Typography>
                  <Grid container spacing={1.5}>
                    {[
                      { type: 'profile_photo', label: 'Profile Photograph', accept: 'image/*' },
                      { type: 'tdg_certificate', label: 'Transportation of Dangerous Goods Certificate', accept: 'image/*,.pdf,application/pdf' },
                      { type: 'driver_license_front', fallbackType: 'driver_license', label: 'Driver License — Front', accept: 'image/*' },
                      { type: 'driver_license_back', label: 'Driver License — Back', accept: 'image/*' },
                    ].map((item) => {
                      const document = documentByType(item.type, item.fallbackType);
                      const isImage = document?.mime_type?.startsWith?.('image/');
                      return (
                        <Grid item xs={12} md={6} key={item.type}>
                          <Box sx={{ p: 1.5, border: '1px solid #e1e8f0', borderRadius: 2, height: '100%' }}>
                            {document?.url && isImage && (
                              <Box
                                component="img"
                                src={document.url}
                                alt={item.label}
                                loading="lazy"
                                sx={{ width: '100%', height: 150, objectFit: item.type === 'profile_photo' ? 'contain' : 'cover', bgcolor: '#f5f8fb', borderRadius: 1.5, border: '1px solid #d8e2ef', mb: 1.25 }}
                              />
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <DescriptionIcon color={document ? 'primary' : 'disabled'} />
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 900 }}>{item.label}</Typography>
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {document?.original_name || 'No file uploaded'}
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Button
                                component="label"
                                size="small"
                                variant="contained"
                                startIcon={<UploadFileIcon />}
                                disabled={Boolean(uploadingDocument)}
                                sx={{ bgcolor: '#001f3f', fontWeight: 900 }}
                              >
                                {uploadingDocument === item.type ? 'Uploading...' : document ? 'Replace' : 'Upload'}
                                <input hidden type="file" accept={item.accept} onChange={uploadDocument(item.type)} />
                              </Button>
                              {document?.url && (
                                <Button size="small" variant="outlined" onClick={() => window.open(document.url, '_blank', 'noopener,noreferrer')}>
                                  Preview
                                </Button>
                              )}
                            </Box>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={saving} onClick={save} sx={{ bgcolor: '#001f3f', fontWeight: 900 }}>
            {saving ? 'Saving…' : 'Save User'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteUser)}
        title="Deactivate User?"
        description={`This will prevent "${deleteUser?.name || 'this user'}" from logging in. Historical records stay unchanged.`}
        confirmText="Deactivate"
        confirmColor="error"
        onConfirm={confirmDelete}
        onClose={() => setDeleteUser(null)}
        icon={<DeleteIcon />}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast((prev) => ({ ...prev, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
